import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import StoreSelector from '../components/StoreSelector'
import { ShoppingList, ShoppingListItem, Product, getProductPrice, getBestPrice, getProductShelfNumber } from '../types'
import { shoppingListsApi, productsApi } from '../services/api'
import toast from 'react-hot-toast'

const ShoppingModePage = () => {
  const { listName } = useParams<{ listName: string }>()
  const navigate = useNavigate()
  const [list, setList] = useState<ShoppingList | null>(null)
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [pickedQuantities, setPickedQuantities] = useState<Map<string, number>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [selectedStore, setSelectedStore] = useState<string>('all')

  useEffect(() => {
    if (listName) {
      loadList()
    }
  }, [listName])

  const loadList = async () => {
    if (!listName) return
    
    setIsLoading(true)
    try {
      const [listsResponse, productsResponse] = await Promise.all([
        shoppingListsApi.getLists(),
        productsApi.getProducts()
      ])

      // Load the specific list
      if (listsResponse.success && listsResponse.data) {
        const lists = Array.isArray(listsResponse.data) ? listsResponse.data : []
        const foundList = lists.find(l => l.name === decodeURIComponent(listName))
        if (foundList) {
          setList(foundList)
          // Auto-select store from first item if available
          if (foundList.items.length > 0 && foundList.items[0].store && selectedStore === 'all') {
            setSelectedStore(foundList.items[0].store)
          }
        } else {
          toast.error('Ostoslistaa ei löytynyt')
          navigate('/shopping-lists')
        }
      } else {
        toast.error('Ostoslistan lataus epäonnistui')
        navigate('/shopping-lists')
      }

      // Load all products for pricing
      if (productsResponse.success && productsResponse.data) {
        const products = Array.isArray(productsResponse.data) ? productsResponse.data : []
        setAllProducts(products)
      }
    } catch (error) {
      console.error('Failed to load shopping list:', error)
      toast.error('Verkkovirhe')
      navigate('/shopping-lists')
    } finally {
      setIsLoading(false)
    }
  }

  const incrementItem = (itemName: string) => {
    setPickedQuantities(prev => {
      const newMap = new Map(prev)
      const currentPicked = newMap.get(itemName) || 0
      newMap.set(itemName, currentPicked + 1)
      return newMap
    })
  }

  const decrementItem = (itemName: string) => {
    setPickedQuantities(prev => {
      const newMap = new Map(prev)
      const currentPicked = newMap.get(itemName) || 0
      if (currentPicked > 0) {
        newMap.set(itemName, currentPicked - 1)
      }
      return newMap
    })
  }

  const getPickedQuantity = (itemName: string): number => {
    return pickedQuantities.get(itemName) || 0
  }

  const calculateProgress = () => {
    if (!list || list.items.length === 0) return 0
    const itemsWithAtLeastOne = list.items.filter(item => getPickedQuantity(item.name) > 0).length
    return Math.round((itemsWithAtLeastOne / list.items.length) * 100)
  }

  const calculatePlannedTotal = () => {
    if (!list) return 0
    return list.items.reduce((total, item) => {
      const currentPrice = getCurrentItemPrice(item)
      return total + ((item.quantity || 0) * currentPrice)
    }, 0)
  }

  const calculateActualTotal = () => {
    if (!list) return 0
    return list.items.reduce((total, item) => {
      const pickedQty = getPickedQuantity(item.name)
      const currentPrice = getCurrentItemPrice(item)
      return total + (pickedQty * currentPrice)
    }, 0)
  }

  const getCurrentItemPrice = (item: ShoppingListItem) => {
    // Find the product to get current pricing
    const product = allProducts.find(p => p.name === item.name)
    if (!product) return item.unitPrice || 0

    if (selectedStore === 'all') {
      return getBestPrice(product).price
    } else {
      return getProductPrice(product, selectedStore)
    }
  }

  const getItemPriceInfo = (item: ShoppingListItem) => {
    const product = allProducts.find(p => p.name === item.name)
    if (!product) {
      return {
        price: item.unitPrice || 0,
        store: item.store || 'Unknown',
        isBestPrice: false,
        isDifferentStore: false
      }
    }

    if (selectedStore === 'all') {
      const best = getBestPrice(product)
      return {
        price: best.price,
        store: best.store,
        isBestPrice: true,
        isDifferentStore: false
      }
    } else {
      const storePrice = product.prices.find(p => p.store === selectedStore)
      if (storePrice) {
        const best = getBestPrice(product)
        return {
          price: storePrice.price,
          store: selectedStore,
          isBestPrice: storePrice.price === best.price,
          isDifferentStore: false
        }
      } else {
        // Show best price with indication it's from different store
        const best = getBestPrice(product)
        return {
          price: best.price,
          store: best.store,
          isBestPrice: false,
          isDifferentStore: true
        }
      }
    }
  }

  const getItemStatus = (item: ShoppingListItem) => {
    const picked = getPickedQuantity(item.name)
    const planned = item.quantity
    
    if (picked === 0) return 'not-picked'
    if (picked < planned) return 'partial'
    if (picked === planned) return 'complete'
    return 'over'
  }

  const getItemStatusColor = (status: string) => {
    switch (status) {
      case 'not-picked': return 'border-gray-200 bg-white'
      case 'partial': return 'border-yellow-200 bg-yellow-50'
      case 'complete': return 'border-green-200 bg-green-50'
      case 'over': return 'border-blue-200 bg-blue-50'
      default: return 'border-gray-200 bg-white'
    }
  }

  const getItemStatusIcon = (status: string) => {
    switch (status) {
      case 'not-picked': 
        return (
          <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center">
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
        )
      case 'partial':
        return (
          <div className="w-6 h-6 rounded-full border-2 border-yellow-500 bg-yellow-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6" />
            </svg>
          </div>
        )
      case 'complete':
        return (
          <div className="w-6 h-6 rounded-full border-2 border-green-500 bg-green-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )
      case 'over':
        return (
          <div className="w-6 h-6 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
        )
      default: return null
    }
  }

  const handleFinish = () => {
    const progress = calculateProgress()
    const actualTotal = calculateActualTotal()
    const plannedTotal = calculatePlannedTotal()
    
    if (progress === 100) {
      if (actualTotal === plannedTotal) {
        toast.success('Ostokset valmiit täsmälleen suunnitelman mukaan! 🎯')
      } else {
        toast.success(`Ostokset valmiit! Budjetti: ${plannedTotal.toFixed(2)}€, Toteutunut: ${actualTotal.toFixed(2)}€ 🛒`)
      }
    } else {
      toast.success(`Ostokset keskeytetty (${progress}% tuotteista kerätty)`)
    }
    navigate('/shopping-lists')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!list) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Listaa ei löytynyt</h3>
        <button 
          onClick={() => navigate('/shopping-lists')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Takaisin listoihin
        </button>
      </div>
    )
  }

  const progress = calculateProgress()
  const total = calculatePlannedTotal()
  const actualTotal = calculateActualTotal()

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{list.name}</h1>
            <p className="text-sm text-gray-600">Kaupassa-tila</p>
          </div>
          <button
            onClick={() => navigate('/shopping-lists')}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Store Selector */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <StoreSelector 
            selectedStore={selectedStore}
            onStoreChange={setSelectedStore}
          />
          {selectedStore !== 'all' && (
            <p className="mt-2 text-sm text-gray-600">
              Ostokset kaupassa: {selectedStore}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm font-medium text-gray-900 mb-1">
            <span>Edistyminen</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{list.items.filter(item => getPickedQuantity(item.name) > 0).length} / {list.items.length} tuotetta</span>
            <span>
              {actualTotal.toFixed(2)} € / {total.toFixed(2)} €
            </span>
          </div>
        </div>
      </div>

      {/* Shopping List Items */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Tuotteet</h2>
          
          {list.items.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Tyhjä lista</p>
          ) : (
            <div className="space-y-3">
              {list.items.map((item, index) => {
                const status = getItemStatus(item)
                const pickedQty = getPickedQuantity(item.name)
                const priceInfo = getItemPriceInfo(item)
                const currentPrice = getCurrentItemPrice(item)
                const product = allProducts.find(p => p.name === item.name)
                const shelfNumber = product ? getProductShelfNumber(product, selectedStore) : ''
                return (
                  <div 
                    key={index}
                    className={`flex items-center p-4 border rounded-lg transition-all ${getItemStatusColor(status)}`}
                  >
                    <div className="flex-shrink-0 mr-4">
                      {getItemStatusIcon(status)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-gray-900">
                            {item.name}
                          </div>
                          {shelfNumber && (
                            <div className="text-sm font-medium text-blue-600 mb-1">
                              📍 Hylly {shelfNumber}
                            </div>
                          )}
                          <div className="text-sm text-gray-500">
                            <span className={`font-medium ${priceInfo.isBestPrice ? 'text-green-600' : 'text-gray-900'}`}>
                              {priceInfo.price.toFixed(2)} €
                              {priceInfo.isBestPrice && <span className="text-xs ml-1">(paras)</span>}
                            </span>
                            {' per kpl'}
                            <span className={priceInfo.isDifferentStore ? 'text-orange-600 ml-1' : 'ml-1'}>
                              • {priceInfo.store}
                              {priceInfo.isDifferentStore && ' (eri kauppa)'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => decrementItem(item.name)}
                              disabled={pickedQty === 0}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </button>
                            
                            <div className="text-center min-w-16">
                              <div className="text-lg font-bold text-gray-900">
                                {pickedQty}/{item.quantity}
                              </div>
                              <div className="text-xs text-gray-500">
                                kerätty/suunn.
                              </div>
                            </div>
                            
                            <button
                              onClick={() => incrementItem(item.name)}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Price */}
                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900">
                              {(pickedQty * currentPrice).toFixed(2)} €
                            </div>
                            {pickedQty !== item.quantity && (
                              <div className="text-xs text-gray-500">
                                ({(item.quantity * currentPrice).toFixed(2)} € suunn.)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm text-gray-600">Yhteensä kerätty:</div>
            <div className="text-2xl font-bold text-green-600">
              {actualTotal.toFixed(2)} €
            </div>
            {actualTotal < total && (
              <div className="text-xs text-gray-500">
                Jäljellä: {(total - actualTotal).toFixed(2)} €
              </div>
            )}
          </div>
          
          <button
            onClick={handleFinish}
            className={`px-6 py-3 rounded-md font-medium ${
              progress === 100
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            {progress === 100 ? 'Valmis! 🎉' : 'Lopeta ostokset'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShoppingModePage 